import React from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  atomDark as dark,
  base16AteliersulphurpoolLight as light,
} from "react-syntax-highlighter/dist/esm/styles/prism";

export const GomokuEmptyBoard = () => {
  const board = Array(15).fill(0).map(() => Array(15).fill(0));
  const boardSize = board.length;
  const lastIndex = boardSize - 1;

  // Star points positions (zero-indexed)
  const starPoints = [
    [3, 3],   [3, 7],   [3, 11],
    [7, 3],   [7, 7],   [7, 11],
    [11, 3],  [11, 7],  [11, 11]
  ];

  return (
    <div className="flex flex-col justify-center">
      <div className="flex justify-center items-center">
        <div className="grid w-fit border-4 border-black">
          {board.map((row, rowIndex) => (
            <div key={rowIndex} className="flex bg-yellow-500 w-fit">
              {row.map((cell, colIndex) => {
                const isFirstRow = rowIndex === 0;
                const isLastRow = rowIndex === lastIndex;
                const isFirstCol = colIndex === 0;
                const isLastCol = colIndex === lastIndex;
                const isEdge = isFirstRow || isLastRow || isFirstCol || isLastCol;

                // Dynamic line positioning
                let horizontalLineClass = "left-0 right-0";
                let verticalLineClass = "top-0 bottom-0";

                if (isFirstRow) {
                  verticalLineClass = "top-1/2 bottom-0";
                  if (isFirstCol) horizontalLineClass = "left-1/2 right-0";
                  else if (isLastCol) horizontalLineClass = "left-0 right-1/2";
                } 
                else if (isLastRow) {
                  verticalLineClass = "top-0 bottom-1/2";
                  if (isFirstCol) horizontalLineClass = "left-1/2 right-0";
                  else if (isLastCol) horizontalLineClass = "left-0 right-1/2";
                }
                else if (isFirstCol) {
                  horizontalLineClass = "left-1/2 right-0";
                }
                else if (isLastCol) {
                  horizontalLineClass = "left-0 right-1/2";
                }

                // Check if current position is a star point
                const isStarPoint = starPoints.some(
                  ([r, c]) => r === rowIndex && c === colIndex
                );

                return (
                  <div
                    key={colIndex}
                    className="relative flex items-center justify-center p-2.5"
                  >
                    {/* Grid Pattern */}
                    <div className="absolute inset-0">
                      <div className={`absolute top-1/2 h-px bg-black -translate-y-1/2 ${horizontalLineClass}`}></div>
                      <div className={`absolute left-1/2 w-px bg-black -translate-x-1/2 ${verticalLineClass}`}></div>
                      
                      {/* Star Point Marker */}
                      {isStarPoint && (
                        <div className="absolute left-1/2 top-1/2 w-1.5 h-1.5 bg-black rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                      )}
                    </div>

                    {/* Player's Piece */}
                    {cell !== 0 && (
                      <div
                        className={`absolute w-3 h-3 rounded-full transform transition-all duration-500 ease-in-out ${
                          cell === 1 ? "bg-black" : "bg-white"
                        }`}
                      ></div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const GomokuDescription = `Connect 5 (horizontally, vertically, or diagonally) of your pieces in a row!`;

export const GomokuProblemStatement = () => {
  const isDarkMode = document.documentElement.classList.contains("dark");

  const gameStateExample = `gameState = [
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 2, 1, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 1, 2, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 1, 0, 2, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 1, 0, 0, 2, 2, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  ]`;

  const gameStateAfterMove = `gameState = [
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 2, 1, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 1, 2, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 1, 0, 2, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 1, 0, 0, 2, 2, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  ]`;

  return (
    <div className="space-y-4">
      <p>
        You must implement a class with a method <code>makeMove()</code> that
        takes in a board and returns the best possible move for the current
        player.
      </p>
      <p>
        A <code>gameState</code> is represented as an 15x15 2D array of integers,
        where 1&apos;s represent player1&apos;s pieces, 2&apos;s represent
        player2&apos;s pieces, and 0&apos;s represent an empty space.
      </p>
      <p>Here is an example:</p>
      {isDarkMode ? (
        <div style={{ fontSize: "16px" }}>
          <SyntaxHighlighter
            language="python"
            style={isDarkMode ? dark : light}
          >
            {gameStateExample}
          </SyntaxHighlighter>
        </div>
      ) : (
        <SyntaxHighlighter language="python">
          {gameStateExample}
        </SyntaxHighlighter>
      )}
      <p>After player1 places a piece, the gameState updates as follows:</p>
      {isDarkMode ? (
        <div style={{ fontSize: "16px" }}>
          <SyntaxHighlighter
            language="python"
            style={isDarkMode ? dark : light}
          >
            {gameStateAfterMove}
          </SyntaxHighlighter>
        </div>
      ) : (
        <SyntaxHighlighter language="python">
          {gameStateAfterMove}
        </SyntaxHighlighter>
      )}
      <p>
        As you can see, player1 wins!
      </p>
      <p>
        Your <code>makeMove()</code> function should return the row and column
        where player1 will place their piece.
      </p>
      <section className="pt-4 pb-4">
        <h2 className="text-lg font-bold dark:text-white text-black">
          3rd party imports:
        </h2>
        <div className="whitespace-pre-wrap">
          {isDarkMode ? (
            <div style={{ fontSize: "16px" }}>
              <SyntaxHighlighter
                language="python"
                style={isDarkMode ? dark : light}
              >
                numpy, torch
              </SyntaxHighlighter>
            </div>
          ) : (
            <SyntaxHighlighter language="python">
              numpy, torch
            </SyntaxHighlighter>
          )}
        </div>
      </section>
      <section className="pb-4">
        <h2 className="text-lg font-bold dark:text-white text-black">
          Constraints:
        </h2>
        <div className="whitespace-pre-wrap">
          <ul className="pl-5 list-piece">
            <li>Time to instantiate class: 2 seconds</li>
            <li>Time per move: 2 seconds</li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export const GomokuVisualMap = {
  1: (
    <div
      className={`w-15 h-15 border-2 rounded-full transform transition-all duration-500 ease-in-out bg-white`}
    ></div>
  ),
  2: (
    <div
      className={`w-15 h-15 border-2 rounded-full transform transition-all duration-500 ease-in-out bg-black`}
    ></div>
  ),
};

export const GomokuStarterCode = `import random
from game import Game

# Random bot
# Update the makeMove function
class Player:
  def __init__(self, player):
    self.player = player

  def makeMove(self, gameState: list[list[int]]) -> list[int]:
    return random.choice(Game.legal_moves(gameState, self.player))
`;

export const GomokuGameCode = `# the gomoku class that will be used to judge your code
class Gomoku:
    def __init__(self):
        self.board = [[0] * 15 for _ in range(15)]
        self.turn = 2  # Black goes first
        self.moves = []

    def detect_win(game_state, turn):
        dirs = [(0,1), (1,0), (1,1), (1,-1)]
        dim = 15
        for x in range(dim):
            for y in range(dim):
                if game_state[x][y] != 0:
                    for dx, dy in dirs:
                        if all(0 <= x+i*dx < dim and 0 <= y+i*dy < dim and 
                               game_state[x+i*dx][y+i*dy] == game_state[x][y] for i in range(5)):
                            return game_state[x][y]

    def legal_moves(game_state, turn):
        if Game.detect_win(game_state, turn) != 0:
            return []
        moves = []
        dim = 15
        for i in range(dim):
            for j in range(dim):
                if (game_state[i][j] == 0):
                    moves.append((i, j))
        return moves

    def make_move(game_state, turn, move):
        x = move[0]
        y = move[1]
        if x < 0 or x >= len(game_state) or y < 0 or y >= len(game_state):
            return game_state
        if move in Game.legal_moves(game_state, turn):
            game_state[x][y] = turn
            #turn = 3 - turn
        return game_state
`;
